const ACCESS_DATA_KEY = "customer-access-data";

let buyerPromise;

/**
 * Builds the BuyerInput for @inContext from the current B2B company location
 * and the logged-in customer. Resolves null when either is missing, or the
 * token exchange fails (storefront falls back to default market pricing).
 * @returns {Promise<{ customerAccessToken: string, companyLocationId: string } | null>}
 */
export default function getBuyer() {
  if (!buyerPromise) {
    buyerPromise = resolveBuyer().catch((error) => {
      console.warn("Unable to resolve B2B buyer", error);
      return null;
    });
  }

  return buyerPromise;
}

async function resolveBuyer() {
  const locationId = window.theme.location_id;

  if (!locationId) return null;

  // cart.js refreshes the Customer Account token when it's near expiry; reading
  // it before that settles can pick up an expired one.
  await window.customerAccessTokenReady;

  const accessData = readAccessData();

  if (!accessData?.access_token) return null;

  let customerAccessToken = accessData.storefront_access_token;

  if (!customerAccessToken) {
    customerAccessToken = await createStorefrontCustomerAccessToken(
      accessData.access_token,
    );

    if (!customerAccessToken) return null;

    cacheStorefrontAccessToken(accessData.access_token, customerAccessToken);
  }

  return {
    customerAccessToken,
    companyLocationId: `gid://shopify/CompanyLocation/${locationId}`,
  };
}

function readAccessData() {
  const customerAccessData = localStorage.getItem(ACCESS_DATA_KEY);
  return customerAccessData ? JSON.parse(customerAccessData) : null;
}

/**
 * Stores the exchanged token on the account token it came from, so the next
 * refresh (which rewrites `customer-access-data`) discards it with the old one.
 */
function cacheStorefrontAccessToken(accountAccessToken, storefrontAccessToken) {
  const latest = readAccessData();

  // Another tab may have refreshed during the exchange — writing back the
  // stale copy would clobber its new refresh token.
  if (latest?.access_token !== accountAccessToken) return;

  localStorage.setItem(
    ACCESS_DATA_KEY,
    JSON.stringify({ ...latest, storefront_access_token: storefrontAccessToken }),
  );
}

/**
 * BuyerInput wants a Storefront API customer token, not the Customer Account
 * API token held in `customer-access-data` — the latter has to be exchanged.
 */
async function createStorefrontCustomerAccessToken(accountAccessToken) {
  const response = await fetch(
    `https://shopify.com/${window.theme.store_id}/account/customer/api/2024-10/graphql`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accountAccessToken,
      },
      body: JSON.stringify({
        query: `mutation storefrontCustomerAccessTokenCreate {
          storefrontCustomerAccessTokenCreate {
            customerAccessToken
            userErrors {
              message
            }
          }
        }`,
      }),
    },
  );
  const { data } = await response.json();
  const { customerAccessToken, userErrors } =
    data?.storefrontCustomerAccessTokenCreate ?? {};

  if (userErrors?.length) {
    console.warn("storefrontCustomerAccessTokenCreate", userErrors);
  }

  return customerAccessToken || null;
}
